/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import isNil from 'lodash/isNil';

import FileSystemPathFactory from '../../file/system/file-system-path-factory';

/**
 * @import BasePathResolver from "../../basepath/basepath";
 * @import { Medium } from "../../medium/medium";
 * @import { FileSystemMapper } from "../../file/system";
 * @import { Url } from '../../url'
 */

/**
 * @param {BasePathResolver} basePathResolver
 * @param {Medium} medium
 * @param {FileSystemMapper} pathMapper
 * @returns {Promise<Url | undefined>}
 */
export async function getUrl(basePathResolver, medium, pathMapper) {
  // Je vyžadována podpora file protokolu na platformě.
  if (!pathMapper.hasFile()) {
    return;
  }
  // Médium musí mít lokálně uložený soubor.
  const pathString = medium.asset?.path;
  if (isNil(pathString)) {
    return;
  }
  const logicalPath = FileSystemPathFactory.fromValue(pathString);

  const physicalPath = await basePathResolver.toPhysicalPath(logicalPath);
  return pathMapper.toFileUrl(physicalPath);
}

/**
 * @param {Medium} medium
 * @returns {boolean}
 */
export function getCheckFile(medium) {
  if (medium.created && medium.asset && medium.asset.lastFetched) {
    if (isNewer(medium.created, medium.asset.lastFetched)) {
      return true;
    }
  }
  // Media mají v detailu média nastavení "Kontrolovat aktuální verzi".
  // Hodnota se předává v parametrech média.
  return /** @type {boolean | undefined} */ (medium.params?.checkFile) ?? false;
}

/**
 * Funkce porovná date1 a date2. True vrací, pokud date1 > date2,
 * jinak false. V případě chyby formátu data je vráceno false.
 * @param {string} date1
 * @param {string} date2
 * @returns {boolean}
 */
function isNewer(date1, date2) {
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getTime() > d2.getTime();
  } catch {
    return false;
  }
}

/**
 * @param {Medium} medium
 * @returns {boolean}
 */
export function isVideoPreloadable(medium) {
  // Zjistí se rozměry média, které ale nemusejí být známé.
  // TODO #555: Pozor, že neřeší kontrolu, zda typ hodnoty je konečné nezáporné číslo.
  const height = /** @type {number | undefined} */ (medium.params?.height);
  const width = /** @type {number | undefined} */ (medium.params?.width);
  if (isNil(height) || isNil(width)) {
    // Pokud nejsou rozměry známé, raději se preload nepoužije.
    return false;
  }
  return Math.max(width, height) <= 1920 && Math.min(width, height) <= 1080;
}
